import { Box, CardContent, Divider, Stack } from '@mui/material';
import { Provider } from '@repo/api-gw-sdk';
import { useState } from 'react';

import {
  EncryptionComponent,
  getEncryptionKey,
} from '@/components/encryption/encryptionComponent';
import { AvailabilityZoneSelect } from '@/components/regions/availabilityZoneSelect';
import { RestoreRegionSelect } from '@/components/regions/restoreRegionSelect';
import { SnapshotToolbar } from '@/components/snapshots/snapshotToolbar';
import { TagsSection } from '@/components/tags/tagsSection';
import { VolumeSettingsSection } from '@/components/volumes/volumeSettings';
import type { StepProps } from '@/components/wizard/StepProps';
import { useDAL } from '@/contexts/useDAL';

import { EonTagRemark } from './eonTagRemark';

import { StepContainer } from '../../wizard/StepContainer';
import { type RestoreVolumeState } from '../restoreVolumeWizard';

export const ConvertToEbsSnapshotConfiguration = (
  props: StepProps<RestoreVolumeState>
) => {
  const dal = useDAL();
  const [error, setError] = useState<string | undefined>();
  const [isRestoring, setIsRestoring] = useState(false);

  const canGoNext = () => {
    if (
      isRestoring ||
      !props.wizardState.regionName ||
      !getEncryptionKey(props.wizardState.encryption)
    ) {
      return false;
    }

    if (
      props.wizardState.actionType === 'volume' &&
      !props.wizardState.availabilityZone
    ) {
      return false;
    }

    return true;
  };

  const onNextClick = async () => {
    setIsRestoring(true);
    setError(undefined);

    const tags =
      (props.wizardState.keepOriginalTags
        ? props.wizardState.initialTags
        : props.wizardState.tags) || {};
    tags['eon:restore'] = 'true';

    if (props.wizardState.actionType === 'convert-snapshot') {
      return dal.convert
        .volume(props.wizardState.resourceId, props.wizardState.snapshot.id, {
          providerVolumeId: props.wizardState.providerVolumeId,
          restoreAccountId: props.wizardState.restoreAccount!.id,
          destination: {
            awsEbs: {
              tags,
              region: props.wizardState.regionName ?? '',
              encryptionKeyId: getEncryptionKey(props.wizardState.encryption),
              snapshotEncryptionKeyId: props.wizardState.encryption?.isEncrypted
                ? getEncryptionKey(props.wizardState.encryption)!
                : '',
            },
          },
        })
        .then(() => props.finish('Convert snapshot has started!'))
        .catch((e) => {
          setIsRestoring(false);
          setError(`Failed to convert volume: ${e.message}`);
        });
    } else if (props.wizardState.actionType === 'volume') {
      const volumeSettings = props.wizardState.keepOriginalSettings
        ? props.wizardState.initialVolumeSettings
        : props.wizardState.volumeSettings;

      const encryptionKey = getEncryptionKey(props.wizardState.encryption);

      return dal.restore
        .volume(props.wizardState.resourceId, props.wizardState.snapshot.id, {
          providerVolumeId: props.wizardState.providerVolumeId,
          restoreAccountId: props.wizardState.restoreAccount!.id,
          destination: {
            awsEbs: {
              tags,
              encryptionKeyId: encryptionKey,
              availabilityZone: props.wizardState.availabilityZone!,
              volumeSettings: volumeSettings!,
              volumeEncryptionKeyId: encryptionKey!,
            },
          },
        })
        .then(() =>
          props.finish(
            `Started restore job for ${props.wizardState.providerVolumeId}`
          )
        )
        .catch((e) => {
          setIsRestoring(false);
          setError(`Failed to restore volume: ${e.message}`);
        });
    }
  };

  return (
    <StepContainer
      sx={{ padding: '0' }}
      navigationComponent={
        error ? (
          <Stack alignItems='center' direction='row' sx={{ color: 'red' }}>
            <i className='ri-error-warning-line mr-[8px] text-[18px]' />
            {error}
          </Stack>
        ) : (
          <EonTagRemark />
        )
      }
      stepperLabels={props.stepperLabels}
      stepperIndex={props.stepperLabels.length - 1}
      nextButtonText='Restore'
      nextButtonLoadingText='Restoring...'
      onBackClick={props.back}
      canGoNext={canGoNext}
      onNextClick={onNextClick}
      toolbarComponent={
        <SnapshotToolbar
          pointInTime={props.wizardState.snapshot.pointInTime}
          vaultId={props.wizardState.snapshot.vaultId}
          accessDeniedItemsCount={
            props.wizardState.snapshot.accessDeniedItemsCount
          }
          supportPiTR={props.wizardState.snapshot.supportPiTR}
        />
      }
    >
      <Box className='mx-[40px] my-[24px]'>
        <Box className='w-full flex justify-between mt-[20px] gap-[36px]'>
          <RestoreRegionSelect
            cloudProvider={Provider.Aws}
            restoreAccountId={props.wizardState.restoreAccount?.id}
            regionName={props.wizardState.regionName}
            onChange={(region) =>
              props.setWizardState((state) => ({
                ...state,
                regionName: region,
                availabilityZone: undefined,
              }))
            }
          />
          {props.wizardState.actionType === 'volume' && (
            <AvailabilityZoneSelect
              accountId={props.wizardState.restoreAccount?.id}
              regionName={props.wizardState.regionName}
              availabilityZone={props.wizardState.availabilityZone}
              onChange={(availabilityZone) =>
                props.setWizardState((state) => ({
                  ...state,
                  availabilityZone,
                }))
              }
            />
          )}
        </Box>
      </Box>
      <Divider />
      <CardContent className='p-0'>
        {props.wizardState.regionName && (
          <EncryptionComponent
            encryption={props.wizardState.encryption}
            accountId={props.wizardState.restoreAccount!.id}
            regionName={props.wizardState.regionName}
            onChange={(encryption) =>
              props.setWizardState((state) => ({
                ...state,
                encryption,
              }))
            }
          />
        )}
      </CardContent>
      {props.wizardState.actionType === 'volume' && (
        <>
          <Divider />
          <CardContent className='p-0'>
            <VolumeSettingsSection
              initialSettings={props.wizardState.initialVolumeSettings}
              settings={props.wizardState.volumeSettings}
              onChange={(volumeSettings) =>
                props.setWizardState((state) => ({ ...state, volumeSettings }))
              }
              keepOriginalSettings={props.wizardState.keepOriginalSettings}
              setKeepOriginalSettings={(value: boolean) =>
                props.setWizardState((state) => ({
                  ...state,
                  keepOriginalSettings: value,
                }))
              }
            />
          </CardContent>
        </>
      )}
      <Divider />
      <CardContent className='p-0'>
        <TagsSection
          initialTags={props.wizardState.initialTags}
          tags={props.wizardState.tags || {}}
          keepOriginalTags={props.wizardState.keepOriginalTags}
          setKeepOriginalTags={(value: boolean) =>
            props.setWizardState((state) => ({
              ...state,
              keepOriginalTags: value,
            }))
          }
          onChange={(tags) =>
            props.setWizardState((state) => ({ ...state, tags }))
          }
        />
      </CardContent>
    </StepContainer>
  );
};
